/**
 * Session management and inactivity timeout handling.
 *
 * Timings are driven by window.sessionTimeoutSeconds (set server-side):
 *
 *   - Warning fires at  = server_timeout - 150 seconds
 *   - Countdown window  = 150 seconds (2.5 minutes)
 *
 * Example with server timeout = 3600s (60 min):
 *   - Modal appears at 57.5 minutes of inactivity with a 2:30 countdown
 *   - Countdown reaches zero → JS navigates to /logout
 *
 * "Stay logged in" calls POST /extend-session to reset the server clock
 * and resets the client timer.
 *
 * Activity detection uses only click and keypress (not mousemove / scroll)
 * so the warning reliably appears for users reading without interacting.
 */

export function initializeSessionManagement() {
  if (!window.userAuthenticated) return;

  const serverTimeout = window.sessionTimeoutSeconds || 60 * 60;
  const COUNTDOWN_SECONDS = 150;
  const WARN_AFTER_MS = (serverTimeout - COUNTDOWN_SECONDS) * 1000;

  const modalEl = document.getElementById('sessionTimeoutModal');
  if (!modalEl) return;

  const bsModal = new bootstrap.Modal(modalEl, { backdrop: 'static', keyboard: false });
  const countdownDisplay = document.getElementById('sessionCountdown');
  const stayBtn = document.getElementById('sessionExtendBtn');
  const logoutBtn = document.getElementById('sessionLogoutBtn');

  let inactivityTimer = null;
  let countdownInterval = null;
  let countdownRemaining = COUNTDOWN_SECONDS;

  function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  function startCountdown() {
    countdownRemaining = COUNTDOWN_SECONDS;
    if (countdownDisplay) {
      countdownDisplay.textContent = formatTime(countdownRemaining);
    }
    clearInterval(countdownInterval);
    countdownInterval = setInterval(() => {
      countdownRemaining -= 1;
      if (countdownDisplay) {
        countdownDisplay.textContent = formatTime(countdownRemaining);
      }
      if (countdownRemaining <= 0) {
        clearInterval(countdownInterval);
        window.location.href = '/logout';
      }
    }, 1000);
  }

  function showWarning() {
    startCountdown();
    bsModal.show();
  }

  function resetInactivityTimer() {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(showWarning, WARN_AFTER_MS);
  }

  function extendSession() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
    fetch('/extend-session', {
      method: 'POST',
      headers: { 'X-CSRFToken': csrfToken },
    })
      .then((response) => {
        if (response.ok) {
          clearInterval(countdownInterval);
          bsModal.hide();
          resetInactivityTimer();
        } else {
          window.location.href = '/logout';
        }
      })
      .catch(() => {
        window.location.href = '/logout';
      });
  }

  if (stayBtn) {
    stayBtn.addEventListener('click', extendSession);
  }
  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      clearInterval(countdownInterval);
      window.location.href = '/logout';
    });
  }

  modalEl.addEventListener('hidden.bs.modal', () => {
    if (countdownRemaining > 0) {
      extendSession();
    }
  });

  ['click', 'keypress'].forEach((event) => {
    document.addEventListener(
      event,
      () => {
        if (!modalEl.classList.contains('show')) {
          resetInactivityTimer();
        }
      },
      { passive: true }
    );
  });

  resetInactivityTimer();
}
